import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { subscriptionService } from "@/services/subscription.service";
import { RoleType } from "@/types/role";

interface SubscriptionContextType {
  checkSubscriptionStatus: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
}) => {
  const { member, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<number>(0);
  const hasRedirectedRef = useRef<boolean>(false);

  const checkSubscriptionStatus = async () => {
    // Don't check if we're already on the subscription expired page
    if (globalThis.location?.pathname === "/dashboard") return;

    // Only check if user is authenticated and not an admin
    if (!isAuthenticated || !member) return;

    const isAdmin =
      member?.roleType === "ADMIN" || member?.roleType === RoleType.Admin;
    if (isAdmin) return;

    // Only allow Family Owners to check subscription status
    if (!member?.family_member_type || member.family_member_type !== "fo") {
      return;
    }

    // Don't check if we've already redirected
    if (hasRedirectedRef.current) return;

    // Throttle checks to avoid too frequent API calls
    const now = Date.now();
    if (now - lastCheckRef.current < 30000) {
      // 30 seconds throttle
      return;
    }
    lastCheckRef.current = now;

    try {
      const subscription = await subscriptionService.getCurrentSubscription();
      const currentDate = new Date();
      const endDate = new Date(subscription.endDate);

      // Check if subscription is expired or cancelled and end date is in the past
      if (
        subscription.status === "expired" ||
        (subscription.status === "cancelled" && endDate < currentDate)
      ) {
        // Set flag, clear interval and redirect to subscription expired page
        hasRedirectedRef.current = true;
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        globalThis.location?.replace("/dashboard");
        return;
      }
    } catch (error) {
      // If subscription check fails, continue with normal flow
      console.warn("Failed to check subscription status:", error);
    }
  };

  useEffect(() => {
    // Don't set up monitoring if we're on the subscription expired page
    if (globalThis.location?.pathname === "/dashboard") return;

    // Don't set up monitoring if already redirected
    if (hasRedirectedRef.current) return;

    // Only set up monitoring if user is authenticated and not admin
    if (!isAuthenticated || !member) return;

    const isAdmin =
      member?.roleType === "ADMIN" || member?.roleType === RoleType.Admin;
    if (isAdmin) return;

    // Reset redirect flag for new session
    hasRedirectedRef.current = false;

    // Initial check when component mounts
    checkSubscriptionStatus();

    // Set up periodic check every 1 hour
    const intervalId = setInterval(checkSubscriptionStatus, 60 * 60 * 1000);
    checkIntervalRef.current = intervalId;

    // Also check when window gains focus (user returns to the tab)
    const handleFocus = () => {
      checkSubscriptionStatus();
    };

    globalThis.addEventListener?.("focus", handleFocus);

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      globalThis.removeEventListener?.("focus", handleFocus);
    };
  }, [isAuthenticated, member]);

  const value = useMemo(
    () => ({
      checkSubscriptionStatus,
    }),
    []
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
